import React, { useState, useEffect, useMemo } from 'react';
import { leadApi, useAuth, apiClient } from '@/lib/api';
import AdditionalNumbersField from '@/components/AdditionalNumbersField';
import LastPickupBadge, { useLastPickup, pickupForPhone } from '@/components/LastPickupBadge';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Phone, Mail, PhoneCall, Calendar, Clock, User, MapPin, Target, Building2, UserPlus, ArrowRightLeft, Send, CheckCircle2, Pencil, X as XIcon, Check as CheckIcon, ShieldAlert, Trash2 } from 'lucide-react';
import { ClickToCall, CallHistory } from './ClickToCall';
import ReactivateLeadModal from './ReactivateLeadModal';
import logger from '@/lib/logger';

const LEAD_STAGES = [
    { id: 'new_lead', label: 'New Lead', color: 'bg-blue-500' },
    { id: 'no_answer', label: 'No Answer', color: 'bg-gray-500' },
    { id: 'call_back', label: 'Call Back', color: 'bg-purple-500' },
    { id: 'warm_lead', label: 'Warm Lead', color: 'bg-yellow-500' },
    { id: 'hot_lead', label: 'Hot Lead', color: 'bg-orange-500' },
    { id: 'in_progress', label: 'In Progress', color: 'bg-cyan-500' },
    { id: 'rejected', label: 'Rejected', color: 'bg-red-500' },
    { id: 'enrolled', label: 'Enrolled', color: 'bg-emerald-500' },
];

const REJECTION_REASONS = [
    { id: 'not_interested', label: 'Not Interested' },
    { id: 'budget_issue', label: 'Budget Issue' },
    { id: 'wrong_number', label: 'Wrong Number' },
    { id: 'already_enrolled', label: 'Already Enrolled Elsewhere' },
    { id: 'no_time', label: 'No Time' },
    { id: 'location_issue', label: 'Location Issue' },
    { id: 'other', label: 'Other' },
];

// -- Always-visible Secondary Phone field for the Lead Detail modal.
//    Rendered outside the notes UI so notes can never overwrite it.
//    Self-sufficient: calls leadApi.update directly. Parent passes
//    `onRefresh()` to re-fetch (optional).
export const SecondaryPhoneField = ({ leadId, value, primaryPhone, onRefresh, pickup, isWinner }) => {
    const [draft, setDraft] = useState(value || '');
    const [saved, setSaved] = useState(value || '');
    const [saving, setSaving] = useState(false);
    useEffect(() => { setDraft(value || ''); setSaved(value || ''); }, [value, leadId]);

    const submit = async (e, override) => {
        e?.preventDefault?.();
        const trimmed = (override !== undefined ? override : draft || '').trim();
        if (trimmed === saved.trim()) return;
        if (trimmed && trimmed.replace(/\D/g, '').length < 7) {
            toast.error('Phone must be at least 7 digits');
            return;
        }
        setSaving(true);
        try {
            await leadApi.update(leadId, { secondary_phone: trimmed });
            setSaved(trimmed);
            setDraft(trimmed);
            toast.success(trimmed ? 'Secondary number saved' : 'Secondary number cleared');
            onRefresh?.(trimmed);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to save secondary number');
        } finally {
            setSaving(false);
        }
    };

    const isDirty = (draft || '').trim() !== (saved || '').trim();

    return (
        <div
            className="mt-4 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2"
            data-testid={`lead-detail-secondary-editor-${leadId}`}
        >
            <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 mb-1.5 inline-flex items-center gap-1.5">
                <Phone className="h-3 w-3" /> Secondary Number
                {pickup && <LastPickupBadge pickup={pickup} isWinner={isWinner} />}
            </div>
            <form onSubmit={submit} className="flex items-center gap-2">
                <Input
                    type="tel"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="+9715XXXXXXXX  (saved to its own field — notes won't touch it)"
                    className="h-8 text-sm font-mono bg-white"
                    data-testid={`lead-detail-secondary-input-${leadId}`}
                />
                <Button
                    type="submit"
                    size="sm"
                    className="h-8 px-3 text-xs"
                    disabled={saving || !isDirty}
                    data-testid={`lead-detail-secondary-save-${leadId}`}
                >
                    {saving ? 'Saving…' : saved ? 'Update' : 'Save'}
                </Button>
                {saved && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        disabled={saving}
                        onClick={(e) => submit(e, '')}
                        data-testid={`lead-detail-secondary-clear-${leadId}`}
                        title="Remove secondary number"
                    >
                        Clear
                    </Button>
                )}
            </form>
            {primaryPhone && (
                <div className="text-[10px] text-muted-foreground mt-1">
                    Primary on file: <span className="font-mono">{primaryPhone}</span>
                </div>
            )}
        </div>
    );
};


// Inline primary-phone editor used in the Lead Details header. Visible
// only to Super Admin / Admin / Sales Manager / Team Leader — sales
// executives never see the pencil. Lets privileged users fix typo'd
// or partially-imported numbers (e.g. missing digit) directly from the
// detail dialog without bouncing to a separate admin tool.
const PrimaryPhoneInlineEditor = ({ leadId, value, canEdit, onSaved }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || '');
    const [saving, setSaving] = useState(false);

    useEffect(() => { setDraft(value || ''); setEditing(false); }, [value, leadId]);

    if (!canEdit) {
        return <span className="truncate">{value || '—'}</span>;
    }

    if (!editing) {
        return (
            <span className="flex items-center gap-1.5">
                <span className="truncate">{value || '—'}</span>
                <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="text-muted-foreground hover:text-primary transition-colors"
                    title="Edit primary phone"
                    data-testid={`lead-detail-primary-phone-edit-${leadId}`}
                >
                    <Pencil className="h-3.5 w-3.5" />
                </button>
            </span>
        );
    }

    const cancel = () => { setDraft(value || ''); setEditing(false); };
    const save = async () => {
        const trimmed = (draft || '').trim();
        if (trimmed === (value || '').trim()) { setEditing(false); return; }
        const digits = trimmed.replace(/\D/g, '');
        if (!trimmed || digits.length < 7) {
            toast.error('Phone must be at least 7 digits');
            return;
        }
        setSaving(true);
        try {
            await leadApi.update(leadId, { phone: trimmed });
            toast.success('Primary phone updated');
            setEditing(false);
            onSaved?.(trimmed);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to update primary phone');
        } finally {
            setSaving(false);
        }
    };

    return (
        <span className="flex items-center gap-1.5">
            <Input
                type="tel"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
                placeholder="+9715XXXXXXXX"
                autoFocus
                disabled={saving}
                className="h-7 w-44 text-xs font-mono"
                data-testid={`lead-detail-primary-phone-input-${leadId}`}
            />
            <button
                type="button"
                onClick={save}
                disabled={saving}
                className="text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                title="Save"
                data-testid={`lead-detail-primary-phone-save-${leadId}`}
            >
                <CheckIcon className="h-4 w-4" />
            </button>
            <button
                type="button"
                onClick={cancel}
                disabled={saving}
                className="text-muted-foreground hover:text-red-600"
                title="Cancel"
                data-testid={`lead-detail-primary-phone-cancel-${leadId}`}
            >
                <XIcon className="h-4 w-4" />
            </button>
        </span>
    );
};


const LeadDetailModal = ({ open, onClose, lead, onUpdate }) => {
    const { user } = useAuth();

    // Can reassign if user is team_leader, sales_manager, admin, or super_admin
    const canReassign = user && ['team_leader', 'sales_manager', 'admin', 'super_admin'].includes(user.role);
    // Mirrors the backend guard for PUT /leads/{id} (phone field). The
    // pencil-edit icon next to the primary phone in the header is only
    // rendered for these roles; sales execs see a read-only span.
    const canEditPrimaryPhone = user && ['super_admin', 'admin', 'sales_manager', 'team_leader'].includes(user.role);

    // Fetch last-pickup map once the modal is open. Powers the green
    // "Picked up" badges so reps know which number the customer answered.
    const pickup = useLastPickup(lead?.id, !!(open && lead?.id));
    
    const [updateData, setUpdateData] = useState({
        stage: '',
        call_notes: '',
        rejection_reason: '',
        follow_up_date: '',
        interested_course_id: '',
        estimated_value: '',
    });
    const [loading, setLoading] = useState(false);
    const [courses, setCourses] = useState([]);
    
    // Stages that require course selection
    const PIPELINE_STAGES = ['warm_lead', 'hot_lead', 'in_progress'];
    
    // Reassignment state
    const [showReassign, setShowReassign] = useState(false);
    const [availableAgents, setAvailableAgents] = useState([]);
    const [reassignData, setReassignData] = useState({
        new_agent_id: '',
        reason: ''
    });
    const [reassignLoading, setReassignLoading] = useState(false);

    // Reactivation state — when a super_admin / sales_manager revives a
    // rejected lead from the stage dropdown, we open ReactivateLeadModal
    // instead of firing a plain PUT (which the backend would reject).
    const canReactivateRejected = user && ['super_admin', 'sales_manager'].includes(user.role);
    const [showReactivate, setShowReactivate] = useState(false);
    const [reactivateTargetStage, setReactivateTargetStage] = useState(null);

    // Super-admin only actions: force-reassign at any stage + delete lead.
    // These are the escape hatches for orphaned/duplicate/wrong-data leads
    // that the normal reassignment workflow can't handle.
    const isSuperAdmin = user?.role === 'super_admin';
    const [showForceAssign, setShowForceAssign] = useState(false);
    const [allActiveUsers, setAllActiveUsers] = useState([]);
    const [forceAssignData, setForceAssignData] = useState({ new_agent_id: '', reason: '' });
    const [forceAssignLoading, setForceAssignLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Memoize filtered agent list to avoid re-computing on every render
    const filteredAgents = useMemo(
        () => availableAgents.filter(a => a.id !== lead?.assigned_to),
        [availableAgents, lead?.assigned_to]
    );

    useEffect(() => {
        if (lead) {
            setUpdateData({
                stage: lead.stage || '',
                call_notes: '',
                rejection_reason: lead.rejection_reason || '',
                follow_up_date: '',
                interested_course_id: lead.interested_course_id || '',
                estimated_value: lead.estimated_value || '',
            });
            setShowReassign(false);
            setReassignData({ new_agent_id: '', reason: '' });
        }
    }, [lead]);
    
    // Fetch courses on mount
    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const res = await apiClient.get('/course-catalog');
                const grouped = res.data?.grouped || {};
                setCourses(grouped.courses || res.data?.items || []);
            } catch (error) {
                logger.error('Failed to fetch course catalog:', error);
            }
        };
        fetchCourses();
    }, []);

    useEffect(() => {
        if (showReassign && canReassign) {
            fetchAvailableAgents();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showReassign]);

    const fetchAvailableAgents = async () => {
        try {
            const res = await apiClient.get('/leads/reassignment/available-agents');
            setAvailableAgents(res.data || []);
        } catch (error) {
            logger.error('Failed to fetch agents:', error);
            toast.error('Failed to load available agents');
        }
    };

    // Superadmin force-assign: fetch the full active users list (not just
    // the normal "available agents" filter, so super-admin can force-assign
    // to inactive teams, mentors, etc. if needed).
    useEffect(() => {
        if (showForceAssign && isSuperAdmin) {
            (async () => {
                try {
                    const res = await apiClient.get('/users');
                    const list = (res.data || []).filter(u => u.is_active && u.id !== lead?.assigned_to);
                    setAllActiveUsers(list);
                } catch (err) {
                    logger.error('Failed to fetch users:', err);
                    toast.error('Failed to load users');
                }
            })();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showForceAssign]);

    const handleForceAssign = async () => {
        if (!forceAssignData.new_agent_id) {
            toast.error('Select a user to assign the lead to');
            return;
        }
        try {
            setForceAssignLoading(true);
            const res = await apiClient.post(`/leads/${lead.id}/force-assign`, {
                new_assigned_to: forceAssignData.new_agent_id,
                reason: forceAssignData.reason,
            });
            toast.success(res.data?.message || 'Lead reassigned');
            setShowForceAssign(false);
            setForceAssignData({ new_agent_id: '', reason: '' });
            if (onUpdate) onUpdate();
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reassign lead');
        } finally {
            setForceAssignLoading(false);
        }
    };

    const handleDeleteLead = async () => {
        try {
            setDeleteLoading(true);
            await apiClient.delete(`/leads/${lead.id}`);
            toast.success('Lead permanently deleted');
            setDeleteConfirm(false);
            if (onUpdate) onUpdate();
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete lead');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleRequestReassignment = async () => {
        if (!reassignData.new_agent_id) {
            toast.error('Please select an agent to reassign to');
            return;
        }
        if (!reassignData.reason.trim()) {
            toast.error('Please provide a reason for reassignment');
            return;
        }

        try {
            setReassignLoading(true);
            const res = await apiClient.post(`/leads/${lead.id}/reassignment-request`, {
                lead_id: lead.id,
                current_agent_id: lead.assigned_to || '',
                new_agent_id: reassignData.new_agent_id,
                reason: reassignData.reason
            });
            
            if (res.data.requires_approval) {
                toast.success(res.data.message, { duration: 5000 });
            } else {
                toast.success(res.data.message);
                if (onUpdate) onUpdate();
            }
            setShowReassign(false);
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit reassignment request');
        } finally {
            setReassignLoading(false);
        }
    };

    const handleUpdateLead = async () => {
        if (!lead) return;

        // REACTIVATION intercept: rejected/not_interested → active stage.
        // Super admin & sales manager only. Open the reassign+reactivate
        // modal; skip the plain PUT which the backend would reject anyway.
        const isReviving = ['rejected', 'not_interested'].includes(lead.stage) &&
                           updateData.stage &&
                           updateData.stage !== lead.stage &&
                           !['rejected', 'not_interested', 'enrolled'].includes(updateData.stage);
        if (isReviving) {
            if (!canReactivateRejected) {
                toast.error('Only Super Admin or Sales Manager can reactivate rejected leads');
                return;
            }
            setReactivateTargetStage(updateData.stage);
            setShowReactivate(true);
            return;
        }

        const updates = {};
        if (updateData.stage && updateData.stage !== lead.stage) {
            updates.stage = updateData.stage;
        }
        if (updateData.call_notes) {
            updates.call_notes = updateData.call_notes;
        }
        if (updateData.stage === 'rejected' && updateData.rejection_reason) {
            updates.rejection_reason = updateData.rejection_reason;
        }
        if (updateData.follow_up_date) {
            updates.follow_up_date = updateData.follow_up_date;
        }
        
        // Add course interest for pipeline stages
        if (updateData.interested_course_id) {
            updates.interested_course_id = updateData.interested_course_id;
            const selectedCourse = courses.find(c => c.id === updateData.interested_course_id);
            if (selectedCourse) {
                updates.interested_course_name = selectedCourse.name;
                // Auto-set estimated value if not manually specified
                if (!updateData.estimated_value) {
                    updates.estimated_value = selectedCourse.price || selectedCourse.base_price || 0;
                }
            }
        }
        if (updateData.estimated_value) {
            updates.estimated_value = parseFloat(updateData.estimated_value);
        }
        
        if (Object.keys(updates).length === 0) {
            toast.error('No changes to update');
            return;
        }
        
        if (updates.stage === 'rejected' && !updates.rejection_reason) {
            toast.error('Please select a rejection reason');
            return;
        }
        
        // Validate course selection for pipeline stages
        if (PIPELINE_STAGES.includes(updates.stage)) {
            const courseId = updates.interested_course_id || lead.interested_course_id;
            if (!courseId) {
                toast.error('Please select which course/package the client is interested in');
                return;
            }
        }
        
        try {
            setLoading(true);
            await leadApi.update(lead.id, updates);
            toast.success('Lead updated successfully');
            onClose();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update lead');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return new Date(dateStr).toLocaleDateString('en-AE', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (!lead) return null;

    const currentStage = LEAD_STAGES.find(s => s.id === lead.stage);

    return (
        <>
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Lead Details</DialogTitle>
                    <DialogDescription>
                        View and update lead information
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-6">
                    {/* Lead Info */}
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-start gap-4">
                                <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold shrink-0">
                                    {lead.full_name?.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-xl font-semibold truncate">{lead.full_name}</h3>
                                    <div className="flex flex-wrap items-center gap-4 mt-2 text-muted-foreground">
                                        <span className="flex items-center gap-2">
                                            <Phone className="h-4 w-4 shrink-0" />
                                            <PrimaryPhoneInlineEditor
                                                leadId={lead.id}
                                                value={lead.phone}
                                                canEdit={canEditPrimaryPhone}
                                                onSaved={onUpdate}
                                            />
                                            <ClickToCall 
                                                phoneNumber={lead.phone} 
                                                contactId={lead.id} 
                                                contactName={lead.full_name}
                                                variant="outline"
                                                size="sm"
                                                showLabel={false}
                                            />
                                            {(() => {
                                                const p = pickupForPhone(pickup.byPhone, lead.phone);
                                                return p ? (
                                                    <LastPickupBadge
                                                        pickup={p}
                                                        isWinner={pickupForPhone({ [pickup.winnerPhone]: true }, lead.phone) === true}
                                                        compact
                                                    />
                                                ) : null;
                                            })()}
                                        </span>
                                        {/* Secondary phone display + click-to-call.
                                            Show ANY non-empty secondary (consistent with
                                            kanban card). Don't hide it just because it
                                            string-matches the primary — sales execs need
                                            both numbers visible to dial through. */}
                                        {lead.secondary_phone && lead.secondary_phone.trim() && (
                                            <span className="flex items-center gap-2" data-testid={`lead-detail-secondary-${lead.id}`}>
                                                <Phone className="h-4 w-4 shrink-0 text-amber-500" />
                                                <span className="text-[10px] text-amber-600 mr-1">2nd</span>
                                                <span className="truncate">{lead.secondary_phone}</span>
                                                <ClickToCall
                                                    phoneNumber={lead.secondary_phone}
                                                    contactId={lead.id}
                                                    contactName={lead.full_name}
                                                    variant="outline"
                                                    size="sm"
                                                    showLabel={false}
                                                />
                                            </span>
                                        )}
                                        {lead.email && (
                                            <span className="flex items-center gap-1">
                                                <Mail className="h-4 w-4 shrink-0" />
                                                <span className="truncate">{lead.email}</span>
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 mt-3">
                                        <Badge className={currentStage?.color}>
                                            {currentStage?.label || lead.stage}
                                        </Badge>
                                        {lead.sla_breach && (
                                            <Badge className="bg-red-500">SLA Breach</Badge>
                                        )}
                                        {lead.in_pool && (
                                            <Badge variant="outline">In Pool</Badge>
                                        )}
                                    </div>

                                    {/* Always-visible Secondary Phone editor.
                                        Lives outside the notes block so agents
                                        adding notes can never overwrite it. */}
                                    <SecondaryPhoneField
                                        leadId={lead.id}
                                        value={lead.secondary_phone || ''}
                                        primaryPhone={lead.phone}
                                        onRefresh={onUpdate}
                                        pickup={pickupForPhone(pickup.byPhone, lead.secondary_phone)}
                                        isWinner={
                                            !!lead.secondary_phone &&
                                            pickupForPhone({ [pickup.winnerPhone]: true }, lead.secondary_phone) === true
                                        }
                                    />
                                    <AdditionalNumbersField
                                        leadId={lead.id}
                                        value={lead.additional_numbers || []}
                                        leadName={lead.full_name}
                                        onRefresh={onUpdate}
                                        byPickup={pickup.byPhone}
                                        winnerPhone={pickup.winnerPhone}
                                    />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
                                <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Country:</span>
                                    <span>{lead.country || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Target className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Source:</span>
                                    <span>{lead.lead_source || lead.source || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Course:</span>
                                    <span>{lead.course_of_interest || lead.course_name || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <User className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Assigned:</span>
                                    <span>{lead.assigned_to_name || 'Unassigned'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Created:</span>
                                    <span>{formatDate(lead.created_at)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Last Activity:</span>
                                    <span>{formatDate(lead.last_activity)}</span>
                                </div>
                                {lead.follow_up_date && (
                                    <div className="flex items-center gap-2 col-span-2">
                                        <Calendar className="h-4 w-4 text-amber-500" />
                                        <span className="text-amber-600 font-medium">Follow-up:</span>
                                        <span className="text-amber-600">{formatDate(lead.follow_up_date)}</span>
                                    </div>
                                )}
                            </div>
                            
                            {/* 3CX Call History */}
                            <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-muted-foreground/20">
                                <div className="flex items-center gap-2 mb-3">
                                    <PhoneCall className="h-4 w-4 text-primary" />
                                    <Label className="text-sm font-medium">3CX Call Center</Label>
                                    <Badge variant="outline" className="text-xs ml-auto bg-green-500/10 text-green-600 border-green-500/30">Connected</Badge>
                                </div>
                                
                                {lead.call_recording_url && (
                                    <div className="mb-3">
                                        <Label className="text-xs text-muted-foreground">Latest Recording</Label>
                                        <a 
                                            href={lead.call_recording_url} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 text-sm text-primary hover:underline mt-1"
                                        >
                                            🎵 Play Recording
                                        </a>
                                    </div>
                                )}
                                
                                <CallHistory contactId={lead.id} />
                            </div>
                            
                            {lead.notes && (
                                <div className="mt-4 p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground text-sm">Notes:</span>
                                    <p className="mt-1 whitespace-pre-wrap">{
                                        typeof lead.notes === 'string'
                                            ? lead.notes
                                            : Array.isArray(lead.notes)
                                                ? lead.notes
                                                    .map(n => (typeof n === 'string' ? n : (n?.text || JSON.stringify(n))))
                                                    .filter(Boolean)
                                                    .join('\n')
                                                : (lead.notes?.text || '')
                                    }</p>
                                </div>
                            )}
                            
                            {lead.call_notes && (
                                <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                                    <span className="text-blue-600 text-sm font-medium">Latest Call Notes:</span>
                                    <p className="mt-1 text-sm">{lead.call_notes}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    
                    {/* Update Section */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">Update Lead</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Move to Stage</Label>
                                    <Select
                                        value={updateData.stage}
                                        onValueChange={(value) => setUpdateData({ ...updateData, stage: value })}
                                    >
                                        <SelectTrigger data-testid="update-stage-select">
                                            <SelectValue placeholder="Select stage" />
                                        </SelectTrigger>
                                        <SelectContent position="popper" className="z-[9999]">
                                            {LEAD_STAGES.map((stage) => (
                                                <SelectItem key={stage.id} value={stage.id}>
                                                    {stage.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                
                                {updateData.stage === 'rejected' && (
                                    <div className="space-y-2">
                                        <Label>Rejection Reason *</Label>
                                        <Select
                                            value={updateData.rejection_reason}
                                            onValueChange={(value) => setUpdateData({ ...updateData, rejection_reason: value })}
                                        >
                                            <SelectTrigger data-testid="rejection-reason-select">
                                                <SelectValue placeholder="Select reason" />
                                            </SelectTrigger>
                                            <SelectContent position="popper" className="z-[9999]">
                                                {REJECTION_REASONS.map((reason) => (
                                                    <SelectItem key={reason.id} value={reason.id}>
                                                        {reason.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                
                                {['call_back', 'warm_lead', 'hot_lead', 'in_progress'].includes(updateData.stage) && (
                                    <div className="space-y-2">
                                        <Label>Follow-up Date</Label>
                                        <Input
                                            type="datetime-local"
                                            value={updateData.follow_up_date}
                                            onChange={(e) => setUpdateData({ ...updateData, follow_up_date: e.target.value })}
                                            data-testid="follow-up-date-input"
                                        />
                                    </div>
                                )}
                            </div>
                            
                            {/* Course Interest - Required for pipeline stages */}
                            {PIPELINE_STAGES.includes(updateData.stage) && (
                                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-4">
                                    <div className="flex items-center gap-2 text-amber-600">
                                        <Target className="h-4 w-4" />
                                        <span className="text-sm font-medium">Course Interest Required</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Course/Package Interested *</Label>
                                            <Select
                                                value={updateData.interested_course_id}
                                                onValueChange={(value) => {
                                                    const course = courses.find(c => c.id === value);
                                                    setUpdateData({ 
                                                        ...updateData, 
                                                        interested_course_id: value,
                                                        estimated_value: course?.price || course?.base_price || ''
                                                    });
                                                }}
                                            >
                                                <SelectTrigger data-testid="course-interest-select">
                                                    <SelectValue placeholder="Select course" />
                                                </SelectTrigger>
                                                <SelectContent position="popper" className="z-[9999]">
                                                    {courses.map((course) => (
                                                        <SelectItem key={course.id} value={course.id}>
                                                            {course.name} - AED {(course.price || course.base_price)?.toLocaleString()}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Estimated Value (AED)</Label>
                                            <Input
                                                type="number"
                                                value={updateData.estimated_value}
                                                onChange={(e) => setUpdateData({ ...updateData, estimated_value: e.target.value })}
                                                placeholder="Auto-filled from course"
                                                data-testid="estimated-value-input"
                                            />
                                        </div>
                                    </div>
                                    {lead?.interested_course_name && (
                                        <p className="text-xs text-muted-foreground">
                                            Currently interested in: <span className="font-medium">{lead.interested_course_name}</span>
                                        </p>
                                    )}
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label>Call Notes</Label>
                                <Textarea
                                    value={updateData.call_notes}
                                    onChange={(e) => setUpdateData({ ...updateData, call_notes: e.target.value })}
                                    placeholder="Add call notes..."
                                    rows={3}
                                    data-testid="call-notes-input"
                                />
                            </div>
                        </CardContent>
                    </Card>
                    
                    {/* Reassignment Section - Only for authorized roles */}
                    {canReassign && (
                        <Card className="border-amber-500/30 bg-amber-500/5">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <ArrowRightLeft className="h-5 w-5 text-amber-600" />
                                    Reassign Lead
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {!showReassign ? (
                                    <div>
                                        <p className="text-sm text-muted-foreground mb-3">
                                            Transfer this lead to another sales agent. 
                                            {user?.role === 'team_leader' && (
                                                <span className="text-amber-600"> Requires approval from Sales Manager and CEO.</span>
                                            )}
                                            {user?.role === 'sales_manager' && (
                                                <span className="text-amber-600"> Requires approval from CEO.</span>
                                            )}
                                        </p>
                                        <Button 
                                            variant="outline" 
                                            className="border-amber-500 text-amber-700 hover:bg-amber-500/10"
                                            onClick={() => setShowReassign(true)}
                                            data-testid="show-reassign-btn"
                                        >
                                            <UserPlus className="h-4 w-4 mr-2" />
                                            Request Reassignment
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Reassign To *</Label>
                                            <Select
                                                value={reassignData.new_agent_id}
                                                onValueChange={(value) => setReassignData({ ...reassignData, new_agent_id: value })}
                                            >
                                                <SelectTrigger data-testid="reassign-agent-select">
                                                    <SelectValue placeholder="Select agent" />
                                                </SelectTrigger>
                                                <SelectContent position="popper" className="z-[9999]">
                                                    {filteredAgents.map((agent) => (
                                                            <SelectItem key={agent.id} value={agent.id}>
                                                                <div className="flex items-center gap-2">
                                                                    <span>{agent.full_name}</span>
                                                                    <Badge variant="outline" className="text-xs">
                                                                        {agent.current_lead_count} leads
                                                                    </Badge>
                                                                </div>
                                                            </SelectItem>
                                                        ))
                                                    }
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label>Reason for Reassignment *</Label>
                                            <Textarea
                                                value={reassignData.reason}
                                                onChange={(e) => setReassignData({ ...reassignData, reason: e.target.value })}
                                                placeholder="Explain why this lead should be reassigned..."
                                                rows={2}
                                                data-testid="reassign-reason-input"
                                            />
                                        </div>
                                        
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                onClick={() => {
                                                    setShowReassign(false);
                                                    setReassignData({ new_agent_id: '', reason: '' });
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={handleRequestReassignment}
                                                disabled={reassignLoading}
                                                className="bg-amber-600 hover:bg-amber-700"
                                                data-testid="submit-reassign-btn"
                                            >
                                                {reassignLoading ? (
                                                    'Submitting...'
                                                ) : (
                                                    <>
                                                        <Send className="h-4 w-4 mr-2" />
                                                        Submit Request
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Super Admin escape hatch — force-assign at any stage + delete */}
                    {isSuperAdmin && (
                        <Card className="border-purple-500/30 bg-purple-500/5">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <ShieldAlert className="h-5 w-5 text-purple-600" />
                                    Super Admin Actions
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {!showForceAssign ? (
                                    <div className="space-y-3">
                                        <p className="text-sm text-muted-foreground">
                                            Reassign this lead to <span className="font-semibold">anyone at any stage</span> — including rejected, orphaned, or enrolled leads. Bypasses the normal approval workflow.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            <Button
                                                variant="outline"
                                                className="border-purple-500 text-purple-700 hover:bg-purple-500/10"
                                                onClick={() => setShowForceAssign(true)}
                                                data-testid="show-force-assign-btn"
                                            >
                                                <UserPlus className="h-4 w-4 mr-2" />
                                                Force Reassign
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="border-red-500 text-red-700 hover:bg-red-500/10"
                                                onClick={() => setDeleteConfirm(true)}
                                                data-testid="show-delete-lead-btn"
                                            >
                                                <Trash2 className="h-4 w-4 mr-2" />
                                                Delete Lead
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label>Assign To *</Label>
                                            <Select
                                                value={forceAssignData.new_agent_id}
                                                onValueChange={(value) => setForceAssignData({ ...forceAssignData, new_agent_id: value })}
                                            >
                                                <SelectTrigger data-testid="force-assign-agent-select">
                                                    <SelectValue placeholder="Select any active user" />
                                                </SelectTrigger>
                                                <SelectContent position="popper" className="z-[9999] max-h-72">
                                                    {allActiveUsers.map((u) => (
                                                        <SelectItem key={u.id} value={u.id}>
                                                            <div className="flex items-center gap-2">
                                                                <span>{u.full_name}</span>
                                                                <Badge variant="outline" className="text-xs capitalize">
                                                                    {(u.role || '').replaceAll('_', ' ')}
                                                                </Badge>
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Reason (optional)</Label>
                                            <Textarea
                                                value={forceAssignData.reason}
                                                onChange={(e) => setForceAssignData({ ...forceAssignData, reason: e.target.value })}
                                                placeholder="Recorded in the lead's assignment history for audit"
                                                rows={2}
                                                data-testid="force-assign-reason-input"
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                onClick={() => {
                                                    setShowForceAssign(false);
                                                    setForceAssignData({ new_agent_id: '', reason: '' });
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={handleForceAssign}
                                                disabled={forceAssignLoading}
                                                className="bg-purple-600 hover:bg-purple-700"
                                                data-testid="submit-force-assign-btn"
                                            >
                                                {forceAssignLoading ? 'Reassigning…' : 'Reassign Now'}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdateLead} disabled={loading} data-testid="update-lead-btn">
                            {loading ? 'Updating...' : 'Update Lead'}
                        </Button>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>

            {/* Reactivate Rejected Lead Modal (super_admin / sales_manager only) */}
            <ReactivateLeadModal
                open={showReactivate}
                lead={lead}
                defaultStage={reactivateTargetStage}
                onClose={() => { setShowReactivate(false); setReactivateTargetStage(null); }}
                onSuccess={() => {
                    if (onUpdate) onUpdate();
                    onClose();
                }}
            />

            {/* Super Admin: delete lead confirmation */}
            <Dialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
                <DialogContent className="max-w-md" data-testid="delete-lead-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Trash2 className="h-5 w-5 text-red-600" />
                            Delete lead permanently?
                        </DialogTitle>
                        <DialogDescription>
                            This deletes <span className="font-semibold">{lead?.full_name || 'this lead'}</span> and all associated call history, notes, and assignment records from the ERP. This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirm(false)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteLead}
                            disabled={deleteLoading}
                            data-testid="confirm-delete-lead-btn"
                        >
                            {deleteLoading ? 'Deleting…' : 'Delete permanently'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default LeadDetailModal;
